import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getConfig } from './config';
import { saveAuthToken, getAccessToken } from './utils/auth';

/**
 * OAuth Callback Handler Page
 *
 * This page handles the redirect from Google after user consent.
 * Responsibilities:
 * - Extract code and state from URL query parameters (T007)
 * - Validate state token against sessionStorage (T009)
 * - Route to appropriate handler based on flow type (T010)
 * - Display loading state during processing
 * - Handle errors and display user-friendly messages
 *
 * Flow types:
 * - 'waitlist': POST /waitlist/google → redirect to landing with confirmation
 * - 'login': POST /auth/google/callback → save JWT → redirect to dashboard
 * - 'link': POST /auth/google/link → redirect to settings with success message
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const config = getConfig();

  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('Processing Google login...');

  // Guard against React 18 StrictMode double-invoking the effect in development,
  // which would consume the one-time OAuth state token twice and surface a
  // spurious "Invalid or expired state token" error on the second call.
  const hasProcessedRef = useRef(false);

  /**
   * User Story 1: Join waitlist via Google (Phase 3)
   * POST /waitlist/google with code and state
   */
  const handleWaitlistFlow = useCallback(async (code, state) => {
    try {
      await axios.post(
        `${config.apiBaseUrl}/waitlist/google`,
        {
          code,
          state
        }
      );

      // Success - user added to waitlist
      setStatus('success');
      setMessage('Thank you for your interest in Dissona! We\'ll review your request and get back to you shortly.');

      // Clear OAuth session data
      sessionStorage.removeItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_flow');
      sessionStorage.removeItem('google_oauth_initiated_at');

      // Redirect to home page after 5 seconds (give user time to read)
      setTimeout(() => navigate('/'), 5000);
    } catch (err) {
      console.error('Waitlist signup error:', err);
      console.error('Response data:', err.response?.data);

      // Handle specific error cases
      if (err.response?.status === 409) {
        // Already on waitlist
        setStatus('success');
        setMessage('You\'re already on our waitlist! We\'ll be in touch soon.');
        setTimeout(() => navigate('/'), 3000);
      } else if (err.response?.status === 400) {
        // Invalid state token (CSRF attack or expired session)
        setStatus('error');
        setMessage('Security validation failed. Please try again.');
        setTimeout(() => navigate('/'), 3000);
      } else if (err.response?.status === 422) {
        // Validation error - show detailed message
        const detail = err.response?.data?.detail;
        let errorMsg = 'Validation failed. Please try again.';
        if (Array.isArray(detail)) {
          // FastAPI validation error format
          errorMsg = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join('; ');
        } else if (typeof detail === 'string') {
          errorMsg = detail;
        }
        setStatus('error');
        setMessage(errorMsg);
        setTimeout(() => navigate('/'), 5000);
      } else {
        // Generic error
        const errorMsg = err.response?.data?.detail ||
                        err.message ||
                        'Failed to join waitlist. Please try again.';
        setStatus('error');
        setMessage(errorMsg);
        setTimeout(() => navigate('/'), 5000);
      }
    }
  }, [config.apiBaseUrl, navigate]);

  /**
   * User Story 2: Approved user login via Google (Phase 4)
   * POST /auth/google/callback with code and state
   */
  const handleLoginFlow = useCallback(async (code, state) => {
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/auth/google/callback`,
        {
          code,
          state
        }
      );

      // Success - save JWT token and redirect to dashboard
      const { access_token } = response.data;
      saveAuthToken(access_token);

      setStatus('success');
      setMessage('Login successful! Redirecting to dashboard...');

      // Clear OAuth session data
      sessionStorage.removeItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_flow');
      sessionStorage.removeItem('google_oauth_initiated_at');

      // Redirect to dashboard after 1.5 seconds
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      // Handle specific error cases
      if (err.response?.status === 403) {
        // User not approved yet (still on waitlist)
        setStatus('error');
        setMessage('Your account is pending approval. Please wait for admin approval or check your email for updates.');
        setTimeout(() => navigate('/login'), 5000);
      } else if (err.response?.status === 404) {
        // No Google account linked
        setStatus('error');
        setMessage('No account found with this Google account. Please sign up first or use username/password login.');
        setTimeout(() => navigate('/login'), 5000);
      } else if (err.response?.status === 400) {
        // Invalid state token
        setStatus('error');
        const errorDetail = err.response?.data?.detail || 'Security validation failed';
        setMessage(`${errorDetail}. Please try again.`);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        // Generic error
        const errorMsg = err.response?.data?.detail ||
                        err.message ||
                        'Login failed. Please try again.';
        setStatus('error');
        setMessage(errorMsg);
        setTimeout(() => navigate('/login'), 5000);
      }
    }
  }, [config.apiBaseUrl, navigate]);

  /**
   * User Story 3: Link Google account to existing password account (Phase 5)
   * POST /auth/google/link with code, state, and auth token
   */
  const handleLinkFlow = useCallback(async (code, state) => {
    try {
      const token = getAccessToken();

      if (!token) {
        setStatus('error');
        setMessage('Authentication required. Please log in first.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      const response = await axios.post(
        `${config.apiBaseUrl}/auth/google/link`,
        {
          code,
          state
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Success - Google account linked
      setStatus('success');
      setMessage(response.data.message || 'Google account linked successfully!');

      // Clear OAuth session data
      sessionStorage.removeItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_flow');
      sessionStorage.removeItem('google_oauth_initiated_at');

      // Redirect to settings page with success message
      setTimeout(() => {
        navigate('/settings?message=' + encodeURIComponent('Google account linked successfully!'));
      }, 2000);
    } catch (err) {
      console.error('Google account linking error:', err);

      // Clear OAuth session data on error too
      sessionStorage.removeItem('google_oauth_state');
      sessionStorage.removeItem('google_oauth_flow');
      sessionStorage.removeItem('google_oauth_initiated_at');

      // Handle specific error cases
      if (err.response?.status === 409) {
        // Google account already linked (to this or another account)
        setStatus('error');
        setMessage('This Google account is already linked to an account.');
        setTimeout(() => navigate('/settings'), 4000);
      } else if (err.response?.status === 400) {
        // Invalid state token or email mismatch
        const errorDetail = err.response?.data?.detail || '';
        if (errorDetail.includes('email')) {
          setStatus('error');
          setMessage('The email address of your Google account doesn\'t match your Dissona account. Please use a matching Google account.');
        } else {
          setStatus('error');
          setMessage('Security validation failed. Please try again.');
        }
        setTimeout(() => navigate('/settings'), 4000);
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        // Authentication failed
        setStatus('error');
        setMessage('Authentication failed. Please log in again.');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        // Generic error
        const errorMsg = err.response?.data?.detail ||
                        err.message ||
                        'Failed to link Google account. Please try again.';
        setStatus('error');
        setMessage(errorMsg);
        setTimeout(() => navigate('/settings'), 4000);
      }
    }
  }, [config.apiBaseUrl, navigate]);

  /**
   * Handle different OAuth flow types
   * Implementation will be expanded in user story phases
   */
  const handleFlowType = useCallback(async (flowType, code, state) => {
    switch (flowType) {
      case 'waitlist':
        // User Story 1 (Phase 3) - T015-T017
        await handleWaitlistFlow(code, state);
        break;

      case 'login':
        // User Story 2 (Phase 4) - T024-T027
        await handleLoginFlow(code, state);
        break;

      case 'link':
        // User Story 3 (Phase 5) - T035-T037
        await handleLinkFlow(code, state);
        break;

      default:
        throw new Error(`Unknown flow type: ${flowType}`);
    }
  }, [handleWaitlistFlow, handleLoginFlow, handleLinkFlow]);

  useEffect(() => {
    // StrictMode-safe one-shot guard: the OAuth state token is single-use,
    // so re-running this effect would deterministically 400 on the backend.
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    const processOAuthCallback = async () => {
      try {
        // 1. Extract code and state from URL query parameters
        const code = searchParams.get('code');
        const stateFromUrl = searchParams.get('state');

        // Handle user cancellation (Google redirects back without code)
        if (!code) {
          const error = searchParams.get('error');
          if (error === 'access_denied') {
            setStatus('error');
            setMessage('You cancelled the Google login. Redirecting...');
            setTimeout(() => navigate('/'), 3000);
            return;
          }
          throw new Error('No authorization code received from Google');
        }

        // 2. Validate state token (T009 - CSRF protection)
        const storedState = sessionStorage.getItem('google_oauth_state');
        const flowType = sessionStorage.getItem('google_oauth_flow');
        const initiatedAt = sessionStorage.getItem('google_oauth_initiated_at');

        if (!storedState || !flowType) {
          throw new Error('OAuth session expired. Please try again.');
        }

        if (stateFromUrl !== storedState) {
          // Clear potentially compromised session data
          sessionStorage.removeItem('google_oauth_state');
          sessionStorage.removeItem('google_oauth_flow');
          sessionStorage.removeItem('google_oauth_initiated_at');
          throw new Error('Invalid state token. Possible CSRF attack.');
        }

        // Check if OAuth flow is too old (10 minute timeout - T062)
        const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
        if (parseInt(initiatedAt) < tenMinutesAgo) {
          sessionStorage.removeItem('google_oauth_state');
          sessionStorage.removeItem('google_oauth_flow');
          sessionStorage.removeItem('google_oauth_initiated_at');
          throw new Error('OAuth session expired (timeout). Please try again.');
        }

        // 3. Route to appropriate handler based on flow type (T010)
        // Pass storedState to the handler before clearing sessionStorage
        await handleFlowType(flowType, code, storedState);

        // 4. Clear session storage after successful handling
        sessionStorage.removeItem('google_oauth_state');
        sessionStorage.removeItem('google_oauth_flow');
        sessionStorage.removeItem('google_oauth_initiated_at');

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.response?.data?.detail || error.message || 'Authentication failed');

        // Redirect to appropriate page after error
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    };

    processOAuthCallback();
  }, [searchParams, navigate, config.apiBaseUrl, handleFlowType]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <svg className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <h2 className="text-xl font-semibold text-white mb-2">Processing...</h2>
              <p className="text-gray-400">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-500 mb-4">
                <svg className="h-16 w-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Thank You!</h2>
              <p className="text-gray-300 mb-2">{message}</p>
              <p className="text-sm text-gray-400 mt-6">You'll be redirected shortly...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-500 mb-4">
                <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Authentication Failed</h2>
              <p className="text-gray-400">{message}</p>
              <p className="text-sm text-gray-500 mt-4">Redirecting you back...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
